import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const useDeletePaymentRequest = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      // IMPORTANT: Payment Requests can be linked to Expenses.
      // We do NOT delete expenses here (per current behavior), but we must unlink them
      // to avoid FK constraint issues that would prevent deleting the request.
      const { error: unlinkError } = await supabase
        .from('expenses')
        .update({ payment_request_id: null })
        .eq('payment_request_id', id);

      if (unlinkError) {
        throw new Error(`Failed to unlink linked expense(s): ${unlinkError.message}`);
      }

      // Delete payment request items
      const { error: itemsError } = await supabase
        .from('payment_request_items')
        .delete()
        .eq('payment_request_id', id);

      if (itemsError) {
        console.error('Failed to delete payment request items:', itemsError);
      }

      // Delete payment request approvals
      const { error: approvalsError } = await supabase
        .from('payment_request_approvals')
        .delete()
        .eq('payment_request_id', id);

      if (approvalsError) {
        console.error('Failed to delete payment request approvals:', approvalsError);
      }

      // Delete payment request documents
      const { error: docsError } = await supabase
        .from('payment_request_documents')
        .delete()
        .eq('payment_request_id', id);

      if (docsError) {
        console.error('Failed to delete payment request documents:', docsError);
      }

      // Finally delete the payment request itself (request returning rows so we can verify)
      const { data: deleted, error: deleteError } = await supabase
        .from('payment_requests')
        .delete()
        .eq('id', id)
        .select('id');

      if (deleteError) {
        throw new Error(`Failed to delete payment request: ${deleteError.message}`);
      }

      if (!deleted || deleted.length === 0) {
        throw new Error('Payment request could not be deleted (no rows affected).');
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-requests'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expense-stats'] });
      toast.success('Payment request deleted');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete: ${error.message}`);
    },
  });
};
